import { ContentSwitcher, type ContentSwitcherOption } from "@carbon6/c6-frontend-design";
import { useMemo, type ReactNode } from "react";
import { MODE_KEY } from "./episodeTagHotkeys";
import {
  episodeReviewModeStore,
  useEpisodeReviewMode,
  type EpisodeReviewMode,
} from "./episodeReviewModeStore";

const MODE_TEXT = {
  review: {
    label: "리뷰 모드",
    tooltip: "결과·결함·표기·결함 수용·코멘트를 모두 펼쳐 편집합니다.",
  },
  view: { label: "뷰 모드", tooltip: "영상을 넓게 보고 실패 표시와 제외만 바로 고칩니다." },
} as const satisfies Record<EpisodeReviewMode, { label: string; tooltip: string }>;

/**
 * 키 힌트를 **꺼진 쪽에만** 붙인 옵션. `M` 을 누르면 꺼진 쪽으로 전환되기 때문이다.
 *
 * 다른 컨트롤처럼 `<Keycap>` 을 세그먼트 안에 넣을 수 없다(c6 `ContentSwitcherOption.label`
 * 이 `string`). 그래서 힌트를 라벨 문자열에 넣었다. 그 문자열이 접근성 이름이 된다. 보이는
 * 라벨이 있으면 c6 가 `ariaLabel` 을 추가하지 않는다.
 *
 * 켜진 쪽에는 붙이지 않는다. 이미 그 모드이므로 키로 전환할 곳이 없다. 양쪽에 붙이면 `M` 이 두 번
 * 표시되어 어느 쪽으로 가는 키인지 오히려 불분명해진다. 힌트가 항상 정확히 하나이므로 **스위처
 * 총 폭도 거의 변하지 않는다.** 토글하면 가운데 경계만 움직인다.
 */
function modeOptions(active: EpisodeReviewMode): readonly ContentSwitcherOption[] {
  const key = MODE_KEY.toUpperCase();
  return (["review", "view"] as const).map((mode) => ({
    value: mode,
    label: mode === active ? MODE_TEXT[mode].label : `${MODE_TEXT[mode].label} (${key})`,
    tooltip: MODE_TEXT[mode].tooltip,
  }));
}

/**
 * 에피소드 라벨링 모드 스위처. 라벨링 독의 머리 줄 맨 앞에 둔다.
 *
 * 스토어를 직접 읽고 쓰는 얇은 컨트롤이다. 이 스위처가 바꾸는 대상이 그 독이므로 독 안에 둔다.
 * pane 최상단에 두면 컨트롤과 대상이 화면 양 끝에 떨어진다. 그러면 사용자는 무엇이 바뀌는지
 * 알기 어렵다. `M` 키가 같은 스토어를 토글한다.
 */
export function EpisodeReviewModeSwitcher(): ReactNode {
  const mode = useEpisodeReviewMode();
  const options = useMemo(() => modeOptions(mode), [mode]);
  return (
    <ContentSwitcher
      label="에피소드 라벨링 모드"
      size="sm"
      options={options}
      value={mode}
      onValueChange={(next) => episodeReviewModeStore.set(next as EpisodeReviewMode)}
    />
  );
}
